import React, { useState } from 'react';
import { Keyboard, StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import PasswordField from 'src/presentation/components/atoms/PasswordField';
import SubmitButton from 'src/presentation/components/atoms/SubmitButton';
import Translations from 'src/core/utils/Translations';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import Dimens from 'src/core/utils/Dimens';
import type { SettingsStackParamList } from 'src/presentation/navigation/types/NavigationTypes';

interface PasswordErrors {
	password?: string;
	passwordRepeat?: string;
}

const MIN_PASSWORD_LENGTH = 8;

function UpdatePasswordForm() {
	const authStore = useAuthenticationStore();
	const navigation = useNavigation<NativeStackNavigationProp<SettingsStackParamList>>();
	const { userProfile } = authStore;

	const [password, setPassword] = useState<string>('');
	const [passwordRepeat, setPasswordRepeat] = useState<string>('');
	const [errors, setErrors] = useState<PasswordErrors>({});

	const clearError = (field: keyof PasswordErrors) => {
		setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
	};

	const validate = (): PasswordErrors => {
		const nextErrors: PasswordErrors = {};

		if (!password) {
			nextErrors.password = Translations.errorFieldRequired;
		} else if (password.length < MIN_PASSWORD_LENGTH) {
			nextErrors.password = Translations.errorPasswordTooShortMessage;
		}

		if (!passwordRepeat) {
			nextErrors.passwordRepeat = Translations.errorFieldRequired;
		} else if (password !== passwordRepeat) {
			nextErrors.passwordRepeat = Translations.errorPasswordsNotMatchingInline;
		}

		return nextErrors;
	};

	const onSubmit = async () => {
		Keyboard.dismiss();

		// Required fields are checked locally first; the API call only follows for a valid form.
		const nextErrors = validate();
		setErrors(nextErrors);
		if (nextErrors.password || nextErrors.passwordRepeat) return;

		await authStore.updateUser(
			'',
			userProfile?.email ?? '',
			password,
			userProfile?.firstname ?? '',
			userProfile?.lastname ?? '',
		);
		// Return to the main settings screen rather than the profile editor: only the password changed, so landing
		// back on the profile form (with any unsaved name edits still shown) would wrongly suggest the profile was
		// saved too.
		navigation.popToTop();
	};

	return (
		<View>
			<PasswordField
				error={errors.password}
				label={Translations.labelNewPassword}
				placeholder={Translations.placeholderPasswordMinChars}
				required={true}
				value={password}
				onChangeText={(text) => {
					setPassword(text);
					clearError('password');
				}}
			/>
			<PasswordField
				error={errors.passwordRepeat}
				label={Translations.labelNewPasswordRepeat}
				placeholder={Translations.placeholderPasswordRepeat}
				required={true}
				value={passwordRepeat}
				onChangeText={(text) => {
					setPasswordRepeat(text);
					clearError('passwordRepeat');
				}}
			/>

			<SubmitButton
				isLoading={authStore.isLoading}
				label={Translations.buttonChangePassword}
				style={styles.submit}
				onPress={onSubmit}
			/>
		</View>
	);
}

export default UpdatePasswordForm;

const styles = StyleSheet.create({
	submit: {
		marginTop: Dimens.margin32,
		flexDirection: 'row',
		justifyContent: 'center',
	},
});
